import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, useMap, FeatureGroup, Popup, Polygon } from 'react-leaflet';
import L from 'leaflet';
import { EditControl } from "react-leaflet-draw";
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';
import '../styles/GpsTracker.css'; 

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const API_URL = "https://depotaircerdas.com/api/tracker";

const GpsTracker = () => {
    const [auth, setAuth] = useState({ token: null, user: null, isSuperAdmin: false });
    const [loadingAuth, setLoadingAuth] = useState(true);
    const [state, setState] = useState({
        lastPosition: null, gpsTrail: [], deviceStatus: 'Offline', lastSeen: '', isLiveTracking: true,
        historyData: null, startDate: '', endDate: '', totalDistance: 0, avgSpeed: 0,
        totalDuration: '0', isBarVisible: true, geofences: [], fuelEfficiency: 10,
    });
    const drawnItemsRef = useRef(new L.FeatureGroup());
    const mapInstanceRef = useRef(null);

    // === LOGIKA AUTENTIKASI ===
    useEffect(() => {
        const storedToken = localStorage.getItem('token');
        const storedUsername = localStorage.getItem('username');
        const isAdmin = localStorage.getItem('is_super_admin') === 'true';
        if (storedToken && storedUsername) {
            setAuth({ token: storedToken, user: storedUsername, isSuperAdmin: isAdmin });
        }
        setLoadingAuth(false);
    }, []);

    // === FUNGSI-FUNGSI PENGAMBIL DATA & LOGIKA (MENGGUNAKAN useCallback) ===
    const fetchData = useCallback(async (url) => {
        const response = await fetch(url, { headers: { 'Authorization': `Bearer ${auth.token}` } });
        if (response.status === 404) return { success: true, data: [] };
        if (!response.ok) throw new Error(response.statusText);
        return response.json();
    }, [auth.token]);

    const fetchLastPosition = useCallback(() => {
        fetchData(`${API_URL}/last-position/${auth.user}`).then(result => {
            if (result.success && result.data) {
                const { latitude, longitude, timestamp } = result.data;
                setState(s => ({ ...s, lastPosition: [latitude, longitude], lastSeen: new Date(timestamp).toLocaleString(), deviceStatus: 'Online', gpsTrail: [...s.gpsTrail, [latitude, longitude]].slice(-50) }));
            } else { setState(s => ({ ...s, deviceStatus: 'Offline' })); }
        }).catch(() => setState(s => ({ ...s, deviceStatus: 'Offline' })));
    }, [auth.user, fetchData]);

    const fetchDeviceStatus = useCallback(() => {
        fetchData(`${API_URL}/device-status/${auth.user}`).then(result => {
            if (result.success) { const { status, last_seen } = result.data; setState(s => ({ ...s, deviceStatus: status, lastSeen: new Date(last_seen).toLocaleString() })); }
        });
    }, [auth.user, fetchData]);

    const fetchAndDisplayGeofence = useCallback(() => {
        fetchData(`${API_URL}/geofence/${auth.user}`).then(result => {
            if (result.success && result.data) { setState(s => ({ ...s, geofences: result.data.map(g => ({ ...g, coordinates: JSON.parse(g.coordinates).map(c => L.latLng(c[0], c[1])) })) })); }
        }).catch(() => setState(s => ({ ...s, geofences: [] })));
    }, [auth.user, fetchData]);

    const getHistory = useCallback(async () => {
        setState(s => ({ ...s, isLiveTracking: false }));
        if (!state.startDate || !state.endDate) { alert("Harap isi tanggal awal dan akhir."); return; }
        try {
            const [historyRes, summaryRes] = await Promise.all([
                fetch(`${API_URL}/history/${auth.user}?start_date=${state.startDate}&end_date=${state.endDate}`, { headers: { 'Authorization': `Bearer ${auth.token}` } }),
                fetch(`${API_URL}/summary/${auth.user}?start_date=${state.startDate}&end_date=${state.endDate}`, { headers: { 'Authorization': `Bearer ${auth.token}` } }),
            ]);
            if (!historyRes.ok || !summaryRes.ok) throw new Error("Gagal mengambil data riwayat.");
            const [historyResult, summaryResult] = await Promise.all([historyRes.json(), summaryRes.json()]);
            if (historyResult.success && historyResult.data && summaryResult.success && summaryResult.data) {
                setState(s => ({ ...s, historyData: historyResult.data, lastPosition: null, gpsTrail: historyResult.data.map(p => [p.latitude, p.longitude]), totalDistance: summaryResult.data.total_distance_km, avgSpeed: summaryResult.data.average_speed_kmh, totalDuration: formatDuration(summaryResult.data.total_duration_sec) }));
            } else { alert('Tidak ada riwayat perjalanan.'); setState(s => ({ ...s, historyData: null })); }
        } catch (error) { console.error("Gagal mengambil riwayat:", error); alert(`Terjadi kesalahan: ${error.message}`); }
    }, [auth.token, auth.user, state.startDate, state.endDate]);

    const handleDrawCreated = useCallback((e) => {
        const { layer } = e;
        const geofenceName = prompt("Beri nama geofence ini:");
        if (geofenceName) {
            const coordinates = layer.getLatLngs()[0].map(latlng => [latlng.lat, latlng.lng]);
            saveGeofence(geofenceName, coordinates);
        } else { e.target.removeLayer(layer); }
    }, []);

    const saveGeofence = useCallback(async (name, coordinates) => {
        try {
            const response = await fetch(`${API_URL}/geofence`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.token}` },
                body: JSON.stringify({ username: auth.user, name, coordinates })
            });
            if (response.ok) { alert("Geofence berhasil disimpan!"); fetchAndDisplayGeofence(); }
            else { const error = await response.json(); alert(`Gagal menyimpan: ${error.message}`); }
        } catch (error) { console.error("Error saat menyimpan geofence:", error); alert("Terjadi kesalahan."); }
    }, [auth.token, auth.user, fetchAndDisplayGeofence]);
    
    // === LOGIKA useEffect YANG MEMANGGIL FUNGSI-FUNGSI DI ATAS ===
    useEffect(() => {
        if (!state.isLiveTracking || !auth.token || !auth.user) return;
        const fetchAndUpdate = () => { fetchLastPosition(); fetchDeviceStatus(); };
        fetchAndUpdate();
        const interval = setInterval(fetchAndUpdate, 5000);
        return () => clearInterval(interval);
    }, [state.isLiveTracking, auth.token, auth.user, fetchLastPosition, fetchDeviceStatus]);

    useEffect(() => {
        if (auth.token && auth.user) fetchAndDisplayGeofence();
    }, [auth.token, auth.user, fetchAndDisplayGeofence]);

    // ... (Fungsi-fungsi helper lainnya) ...
    const calculateFuel = () => (state.totalDistance > 0 && state.fuelEfficiency > 0) ? (state.totalDistance / state.fuelEfficiency).toFixed(2) : '0';
    const formatDuration = (seconds) => { const h = Math.floor(seconds / 3600); const m = Math.floor((seconds % 3600) / 60); const s = Math.floor(seconds % 60); return `${h} jam ${m} menit ${s} detik`; };
    const getChartData = () => state.historyData ? { labels: state.historyData.map(p => new Date(p.timestamp).toLocaleTimeString()), datasets: [{ label: 'Kecepatan (km/h)', data: state.historyData.map(p => parseFloat(p.speed)), borderColor: 'rgb(75, 192, 192)', tension: 0.1, fill: false }] } : null;

    const MapController = () => {
        const map = useMap();
        useEffect(() => {
            mapInstanceRef.current = map;
            if (state.lastPosition && state.isLiveTracking) map.panTo(state.lastPosition);
            else if (state.gpsTrail.length > 0 && !state.isLiveTracking) { const bounds = L.polyline(state.gpsTrail).getBounds(); if (bounds.isValid()) map.fitBounds(bounds); }
        }, [map, state.lastPosition, state.gpsTrail, state.isLiveTracking]);
        return null;
    };
    
    if (loadingAuth) return <div style={{ padding: '20px' }}>Memuat GPS Tracker...</div>;
    if (!auth.token) return <div style={{ padding: '20px' }}>Akses ditolak. Silakan login.</div>;

    return (
        <div className="gps-tracker-page-container">
            <MapContainer center={[-6.366, 106.880]} zoom={15}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' />
                <MapController />
                <FeatureGroup ref={drawnItemsRef}>
                    {auth.isSuperAdmin && (
                         <EditControl position='topright' onCreated={handleDrawCreated} draw={{ polygon: true, polyline: false, rectangle: false, circle: false, marker: false, circlemarker: false }} />
                    )}
                </FeatureGroup>
                {state.lastPosition && <Marker position={state.lastPosition}><Popup>Posisi Terakhir<br />{state.lastSeen}</Popup></Marker>}
                <Polyline positions={state.gpsTrail} color="red" />
                {state.geofences.map((g, index) => <Polygon key={index} positions={g.coordinates} pathOptions={{ color: 'blue' }}><Popup>{g.name}</Popup></Polygon>)}
            </MapContainer>
            <div className={`bottom-bar ${state.isBarVisible ? 'visible' : 'hidden'}`}>
                <div className="bottom-bar-header"><h3>GPS Tracker</h3><button onClick={() => setState(s => ({...s, isBarVisible: !s.isBarVisible}))}>{state.isBarVisible ? '▼' : '▲'}</button></div>
                <div className="bottom-bar-content">
                    <div className="card"><div className="card-body"><h5 className="card-title">Pilih Perangkat</h5><p><strong>{auth.user}</strong></p></div></div>
                    <div className="card"><div className="card-body"><h5 className="card-title">Status Perangkat</h5><p className="card-text">Perangkat: <strong>{auth.user}</strong></p><p className="card-text">Status: <span className={`badge bg-${state.deviceStatus === 'Online' ? 'success' : 'danger'}`}>{state.deviceStatus}</span></p><p className="card-text">Terakhir terlihat: <span id="lastSeen">{state.lastSeen}</span></p></div></div>
                    <div className="card"><div className="card-body"><h5 className="card-title">Kontrol & Analisis</h5><div className="mb-3"><label htmlFor="startDate" className="form-label">Dari Tanggal:</label><input type="date" id="startDate" className="form-control" value={state.startDate} onChange={(e) => setState(s => ({...s, startDate: e.target.value}))} /></div><div className="mb-3"><label htmlFor="endDate" className="form-label">Hingga Tanggal:</label><input type="date" id="endDate" className="form-control" value={state.endDate} onChange={(e) => setState(s => ({...s, endDate: e.target.value}))} /></div><div className="d-grid gap-2"><button onClick={getHistory} className="btn btn-secondary">Tampilkan Riwayat</button><button onClick={() => setState(s => ({...s, isLiveTracking: true, historyData: null}))} className="btn btn-success">Pelacakan Langsung</button></div></div></div>
                    {state.historyData && (<><div className="stats-panel card mt-3"><div className="card-body"><h5 className="card-title">Statistik Perjalanan</h5><p className="card-text">Total Jarak: <strong>{state.totalDistance}</strong> km</p><p className="card-text">Durasi: <strong>{state.totalDuration}</strong></p><p className="card-text">Kecepatan Rata-rata: <strong>{state.avgSpeed}</strong> km/h</p><hr /><h5 className="card-title">Perkiraan BBM</h5><p className="card-text">Efisiensi (km/L): <input type="number" className="form-control d-inline-block w-auto" value={state.fuelEfficiency} onChange={(e) => setState(s => ({...s, fuelEfficiency: parseFloat(e.target.value)}))} min="1" /></p><p className="card-text">Perkiraan BBM: <strong>{calculateFuel()}</strong> Liter</p></div></div><div className="chart-panel card mt-3"><div className="card-body"><h5 className="card-title">Grafik Kecepatan</h5>{getChartData() && <Line data={getChartData()} />}</div></div></>)}
                </div>
            </div>
        </div>
    );
};

export default GpsTracker;